import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OverlayModule, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Router } from '@angular/router';

import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';
import { AddBankModalComponent } from '../../../../shared/components/add-bank-modal/add-bank-modal';
import { SendUpiModal } from '../../../../shared/components/send-upi-modal/send-upi-modal';
import { ScanQrModal } from '../../../../shared/components/scan-qr-modal/scan-qr-modal';
import { BankAccountModal } from '../../../../shared/components/bank-account-modal/bank-account-modal';
import { OwnAccountModal } from '../../../../shared/components/own-account-modal/own-account-modal';
import { TransferFund } from '../../../transfer-fund/transfer-fund';
import { FiDataService } from '../../../../core/services/fi-data.service';
import { Subscription } from 'rxjs';

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  balance: number;
  type: 'Savings' | 'Credit Card';
  logo: string;
  usedAmount?: number;
  limit?: number;
}

interface BankOption {
  id: string;
  name: string;
  logo: string;
}

@Component({
  selector: 'app-total-balance-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayModule, WidgetEdit],
  templateUrl: './total-balance.html',
  styleUrls: ['./total-balance.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TotalBalanceWidget implements OnInit, OnDestroy {
  @Input() title = 'Total Balance';
  @Input() size: 'small' | 'medium' | 'half' | 'full' = 'medium';
  @Input() index = 0;
  @Input() isEditMode = false;
  @Input() isAddMode = false;
  @Output() addWidget = new EventEmitter<void>();
  @Input() activeDropdownIndex: number | null = null;
  @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
  @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
  @Input() changeWidgetSize!: (index: number, size: any) => void;
  @Input() removeWidget!: (index: number) => void;

  // State
  isDropdownOpen = false;
  isSettingsOpen = false;
  activeTab: 'Accounts' | 'Credit Cards' = 'Accounts';
  searchQuery = '';
  selectedBankId: string | null = null;
  temporarySelectedBankId: string | null = null;
  isSendDropdownOpen = false;
  isRecentTransactionsOpen = false;

  // Overlay Refs
  private addBankOverlayRef: OverlayRef | null = null;
  private upiOverlayRef: OverlayRef | null = null;
  private scanQrOverlayRef: OverlayRef | null = null;
  private bankAccountOverlayRef: OverlayRef | null = null;
  private ownAccountOverlayRef: OverlayRef | null = null;
  private transferFundOverlayRef: OverlayRef | null = null;

  // Subscription bag for all subscriptions
  private subs = new Subscription();

  openBankAccountModal(event?: Event) {
    if (this.isAddMode) return;
    if (event) event.stopPropagation();

    if (!this.bankAccountOverlayRef) {
      this.bankAccountOverlayRef = this.createGlobalOverlay();
      this.subs.add(this.bankAccountOverlayRef.backdropClick().subscribe(() => this.closeBankAccountModal()));

      const portal = new ComponentPortal(BankAccountModal);
      const componentRef = this.bankAccountOverlayRef.attach(portal);

      this.subs.add(componentRef.instance.close.subscribe(() => this.closeBankAccountModal()));
    }
    this.cdr.detectChanges();
  }

  closeBankAccountModal(event?: Event) {
    if (event) event.stopPropagation();
    if (this.bankAccountOverlayRef) {
      this.bankAccountOverlayRef.dispose();
      this.bankAccountOverlayRef = null;
    }
    this.cdr.detectChanges();
  }

  openOwnAccountModal(event?: Event) {
    if (this.isAddMode) return;
    if (event) event.stopPropagation();

    if (!this.ownAccountOverlayRef) {
      this.ownAccountOverlayRef = this.createGlobalOverlay();
      this.subs.add(this.ownAccountOverlayRef.backdropClick().subscribe(() => this.closeOwnAccountModal()));

      const portal = new ComponentPortal(OwnAccountModal);
      const componentRef = this.ownAccountOverlayRef.attach(portal);

      this.subs.add(componentRef.instance.close.subscribe(() => this.closeOwnAccountModal()));
    }
    this.cdr.detectChanges();
  }

  closeOwnAccountModal(event?: Event) {
    if (event) event.stopPropagation();
    if (this.ownAccountOverlayRef) {
      this.ownAccountOverlayRef.dispose();
      this.ownAccountOverlayRef = null;
    }
    this.cdr.detectChanges();
  }

  openTransferFund(event?: Event) {
    if (this.isAddMode) return;
    if (event) event.stopPropagation();

    if (!this.transferFundOverlayRef) {
      this.transferFundOverlayRef = this.createGlobalOverlay();
      this.subs.add(this.transferFundOverlayRef.backdropClick().subscribe(() => this.closeTransferFund()));

      const portal = new ComponentPortal(TransferFund);
      const componentRef = this.transferFundOverlayRef.attach(portal);

      this.subs.add(componentRef.instance.close.subscribe(() => this.closeTransferFund()));
    }
    this.cdr.detectChanges();
  }

  closeTransferFund(event?: Event) {
    if (event) event.stopPropagation();
    if (this.transferFundOverlayRef) {
      this.transferFundOverlayRef.dispose();
      this.transferFundOverlayRef = null;
    }
    this.cdr.detectChanges();
  }

  // Data
  accounts: BankAccount[] = [];

  creditCards: BankAccount[] = [
    {
      id: '3',
      bankName: 'HDFC Credit',
      accountNumber: '**** 5645',
      balance: 40000.00,
      type: 'Credit Card',
      logo: 'assets/icons/img-bank.svg',
      usedAmount: 10000.00,
    },
    {
      id: '4',
      bankName: 'India Bank',
      accountNumber: '**** 5645',
      balance: 0.27,
      type: 'Credit Card',
      logo: 'assets/icons/img-bank.svg',
      usedAmount: 40000.00,
    },
  ];

  transactionCurrentPage = 0;
  transactionPageSize = 5;

  recentTransactions: any[] = [];

  get filteredRecentTransactions() {
    if (this.selectedBankId) {
      return this.recentTransactions.filter(tx => tx.accountId === this.selectedBankId);
    }
    return this.recentTransactions;
  }

  get paginatedRecentTransactions() {
    const start = this.transactionCurrentPage * this.transactionPageSize;
    return this.filteredRecentTransactions.slice(start, start + this.transactionPageSize);
  }

  nextTransactionPage() {
    if ((this.transactionCurrentPage + 1) * this.transactionPageSize < this.recentTransactions.length) {
      this.transactionCurrentPage++;
      this.cdr.detectChanges();
    }
  }

  previousTransactionPage() {
    if (this.transactionCurrentPage > 0) {
      this.transactionCurrentPage--;
      this.cdr.detectChanges();
    }
  }

  weeklyLimit = 2850.75;
  maxWeeklyLimit = 50000;

  constructor(
    private cdr: ChangeDetectorRef,
    private overlay: Overlay,
    private router: Router,
    private fiDataService: FiDataService
  ) { }

  ngOnInit() {
    this.callFIDataAPI();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    // Dispose all overlay references to prevent CDK memory leaks
    [this.addBankOverlayRef, this.upiOverlayRef, this.scanQrOverlayRef,
    this.bankAccountOverlayRef, this.ownAccountOverlayRef, this.transferFundOverlayRef]
      .forEach(ref => { if (ref) ref.dispose(); });
  }

  /**
   * Calls the FI Data API with the consent ID
   */
  callFIDataAPI() {
    let consentID: any = localStorage.getItem('active_consent_id');

    this.subs.add(this.fiDataService.getAllFIData(consentID, 'cache').subscribe({
      next: (response: any) => {

        if (response && response.data && Array.isArray(response.data)) {
          const allBanks: BankAccount[] = response.data.map((item: any) => {
            const summary = item.Summary || {};
            const fiType = (item.fiType || 'UNKNOWN').toUpperCase();
            const subType = (summary.type || summary.accountType || '').toUpperCase();

            let balance = 0;
            let usedAmount = 0;
            let limit = 0;

            // 1. INSTRUMENT-SPECIFIC DATA EXTRACTION (fiType Aware)
            switch (fiType) {
              case 'DEPOSIT': // Savings / Current Accounts
                balance = parseFloat(summary.currentBalance || 0);
                limit = parseFloat(summary.currentODLimit || 0);
                break;

              case 'TERM_DEPOSIT':
              case 'TERM-DEPOSIT':
              case 'RECURRING_DEPOSIT':
              case 'CP':
              case 'GOVT_SECURITIES':
              case 'BONDS':
              case 'DEBENTURES':
                balance = parseFloat(summary.currentValue || summary.principalAmount || 0);
                limit = parseFloat(summary.maturityAmount || 0);
                break;

              case 'MUTUAL_FUNDS':
              case 'EQUITIES':
              case 'ETF':
              case 'SIP':
              case 'IDR':
              case 'CIS':
              case 'AIF':
              case 'INVIT':
              case 'REIT':
                // For these, currentValue is the primary indicator of worth
                balance = parseFloat(summary.currentValue || summary.costValue || 0);
                break;

              case 'NPS':
                // NPS often has value at the top level of summary
                balance = parseFloat(summary.currentValue || 0);
                break;

              case 'INSURANCE_POLICIES':
              case 'LIFE_INSURANCE':
              case 'GENERAL_INSURANCE':
                balance = parseFloat(summary.currentValue || summary.surrenderValue || summary.balance || 0);
                break;

              case 'CREDIT_CARD':
                balance = parseFloat(summary.availableLimit || 0);
                usedAmount = parseFloat(summary.currentOutstanding || summary.usedAmount || 0);
                limit = parseFloat(summary.creditLimit || 0);
                break;

              case 'GSTR1_3B':
                // GST doesn't typically have a "balance", but let's check for any reported value
                balance = parseFloat(summary.currentValue || 0);
                break;

              default:
                // Universal fallback
                balance = parseFloat(summary.currentValue || summary.currentBalance || summary.principalAmount || summary.balance || 0);
                break;
            }

            // 2. UI CATEGORIZATION (Banking vs Cards vs Wealth)
            // Primary banking accounts (CASA)
            const isBanking = fiType === 'DEPOSIT' || subType.includes('SAVINGS') || subType.includes('CURRENT');
            // Credit/Debit Cards
            const isCreditCard = fiType === 'CREDIT_CARD' || subType.includes('CREDIT') || fiType.includes('CARD');
            // Everything else is treated as Wealth/Investment but displayed in 'Accounts' for now

            const uiType: 'Savings' | 'Credit Card' = isCreditCard ? 'Credit Card' : 'Savings';

            return {
              id: item.linkReferenceNumber || Math.random().toString(),
              bankName: item.bank || item.fipName || fiType.replace(/_/g, ' '),
              accountNumber: item.maskedAccountNumber ?
                (item.maskedAccountNumber.includes('XXXXXXXX') ? '**** ' + item.maskedAccountNumber.slice(-4) : item.maskedAccountNumber)
                : '**** ****',
              balance: balance,
              type: uiType,
              logo: 'assets/icons/img-bank.svg',
              usedAmount: usedAmount,
              limit: limit
            };
          });

          this.accounts = allBanks.filter(a => a.type === 'Savings');
          this.creditCards = allBanks.filter(a => a.type === 'Credit Card');

          // Initialize selection
          if (!this.selectedBankId) {
            const firstAvailable = this.accounts.length > 0 ? this.accounts[0] : (this.creditCards.length > 0 ? this.creditCards[0] : null);
            if (firstAvailable) this.setBank(firstAvailable.id);
          }

          // 2. Map Transactions (Supporting diverse field names across types)
          let allTransactions: any[] = [];
          response.data.forEach((item: any) => {
            if (item.Transactions && item.Transactions.Transaction && Array.isArray(item.Transactions.Transaction)) {
              const txsWithId = item.Transactions.Transaction.map((tx: any) => ({
                ...tx,
                accountId: item.linkReferenceNumber
              }));
              allTransactions = [...allTransactions, ...txsWithId];
            }
          });

          this.recentTransactions = allTransactions.map((tx: any) => {
            // Flexible date parsing
            const rawDateStr = tx.transactionTimestamp || tx.transactionDateTime || tx.transactionDate || tx.navDate;
            const dateObj = rawDateStr ? new Date(rawDateStr) : new Date();

            const type = (tx.type || 'DEBIT').toUpperCase();
            const mode = (tx.mode || 'OTHERS').toUpperCase();

            let displayTitle = this.cleanTransactionTitle(tx.narration || tx.isinDescription || tx.nameofAsset || tx.schemePlan || tx.mode || 'Transaction');

            // Refine titles based on common modes
            if (mode === 'ATM') displayTitle = 'ATM withdrawal';
            else if (mode === 'CARD') displayTitle = type === 'DEBIT' ? 'Card withdrawal' : 'Card credit';
            else if (mode === 'UPI') displayTitle = type === 'DEBIT' ? 'UPI payment' : 'UPI receipt';
            else if (mode === 'FT') displayTitle = type === 'DEBIT' ? 'Funds transfer' : 'Funds receipt';
            else if (mode === 'CASH') displayTitle = type === 'DEBIT' ? 'Cash withdrawal' : 'Cash deposit';

            return {
              title: displayTitle,
              date: dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).toUpperCase(),
              time: dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
              // In banking context, 'BUY' usually means cash going out
              amount: (type === 'DEBIT' || type === 'SELL' || type === 'BUY' ? -1 : 1) * parseFloat(tx.amount || 0),
              rawDate: dateObj,
              accountId: tx.accountId
            };
          }).sort((a, b) => b.rawDate.getTime() - a.rawDate.getTime());

          // 3. Set limit from first found account
          const firstAccountWithLimit = allBanks.find(a => a.limit !== undefined && a.limit > 0);
          if (firstAccountWithLimit && firstAccountWithLimit.limit !== undefined) {
            this.maxWeeklyLimit = firstAccountWithLimit.limit;
          }
        }

        this.cdr.detectChanges();
      },
      error: (error) => {
        // console.error('❌ FI Data API - Error:', error);
      }
    }));
  }

  navigateToTransactions(event: Event) {
    if (this.isAddMode) return;
    this.closeRecentTransactions(event);
    this.router.navigate(['/transactions']);
  }

  private createGlobalOverlay(): OverlayRef {
    return this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });
  }

  get currentList(): BankAccount[] {
    return this.activeTab === 'Accounts' ? this.accounts : this.creditCards;
  }

  get displayAccount(): BankAccount | null {
    if (this.selectedBankId) {
      const all = [...this.accounts, ...this.creditCards];
      return all.find(a => a.id === this.selectedBankId) || null;
    }
    return null;
  }

  get totalBalance(): number {
    if (this.selectedBankId) {
      return this.displayAccount?.balance || 0;
    }
    return this.currentList.reduce((sum, acc) => sum + acc.balance, 0);
  }

  get allSavingsBalance(): number {
    return this.accounts.reduce((sum, acc) => sum + acc.balance, 0);
  }

  get allCreditCardsBalance(): number {
    return this.creditCards.reduce((sum, acc) => sum + acc.balance, 0);
  }

  get allCreditCardsUsed(): number {
    return this.creditCards.reduce((sum, acc) => sum + (acc.usedAmount || 0), 0);
  }

  get totalUsed(): number {
    if (this.selectedBankId) {
      return this.displayAccount?.usedAmount || 0;
    }
    return this.currentList.reduce((sum, acc) => sum + (acc.usedAmount || 0), 0);
  }

  setBank(id: string | null) {
    this.selectedBankId = id;
    this.transactionCurrentPage = 0; // Reset pagination for transactions

    // Update the service with the selected account
    this.fiDataService.setSelectedAccount(id);

    // Update the maxWeeklyLimit based on the selected account's currentODLimit
    const acc = this.displayAccount;
    if (acc && acc.limit) {
      this.maxWeeklyLimit = acc.limit;
    } else if (!id && this.accounts.length > 0) {
      // If "All Banks" or no selection but accounts exist, use first account's limit
      this.maxWeeklyLimit = this.accounts[0].limit || 50000;
    }

    this.cdr.detectChanges();
  }

  removeAccount(id: string) {
    this.accounts = this.accounts.filter(a => a.id !== id);
    this.creditCards = this.creditCards.filter(c => c.id !== id);

    // If we deleted the selected bank, select the first available one
    if (this.selectedBankId === id) {
      const firstBank = this.accounts[0] || this.creditCards[0];
      this.setBank(firstBank ? firstBank.id : null);
    }
    this.cdr.detectChanges();
  }

  toggleDropdown(event: Event) {
    if (this.isEditMode || this.isAddMode) return;
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.temporarySelectedBankId = this.selectedBankId;
    }
    this.isSettingsOpen = false;
    this.cdr.detectChanges();
  }

  toggleSettings(event: Event) {
    if (this.isEditMode || this.isAddMode) return;
    event.stopPropagation();
    this.isSettingsOpen = !this.isSettingsOpen;
    if (this.isSettingsOpen) {
      this.temporarySelectedBankId = this.selectedBankId;
    }
    this.isDropdownOpen = false;
    this.cdr.detectChanges();
  }

  closeDropdown(event?: Event) {
    if (event) event.stopPropagation();
    if (this.isDropdownOpen) {
      this.setBank(this.temporarySelectedBankId);
    }
    this.isDropdownOpen = false;
    this.cdr.detectChanges();
  }

  confirmSettings(event?: Event) {
    if (event) event.stopPropagation();
    if (this.isSettingsOpen) {
      this.setBank(this.temporarySelectedBankId);
    }
    this.isSettingsOpen = false;
    this.cdr.detectChanges();
  }

  closeSettings(event?: Event) {
    if (event) event.stopPropagation();
    this.isSettingsOpen = false;
    this.cdr.detectChanges();
  }

  toggleSendDropdown(event: Event) {
    if (this.isEditMode || this.isAddMode) return;
    event.stopPropagation();
    this.isSendDropdownOpen = !this.isSendDropdownOpen;
    this.isDropdownOpen = false;
    this.isSettingsOpen = false;
    this.cdr.detectChanges();
  }

  closeSendDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.isSendDropdownOpen = false;
    this.cdr.detectChanges();
  }

  toggleRecentTransactions(event: Event) {
    if (this.isEditMode || this.isAddMode) return;
    event.stopPropagation();
    this.isRecentTransactionsOpen = !this.isRecentTransactionsOpen;
    this.isSendDropdownOpen = false;
    this.isDropdownOpen = false;
    this.isSettingsOpen = false;
    this.cdr.detectChanges();
  }

  closeRecentTransactions(event?: Event) {
    if (event) event.stopPropagation();
    this.isRecentTransactionsOpen = false;
    this.cdr.detectChanges();
  }

  switchTab(tab: 'Accounts' | 'Credit Cards') {
    this.activeTab = tab;
  }

  isAddingBank = false;

  openAddBankModal(event?: Event) {
    if (this.isAddMode || this.isAddingBank) return;
    if (event) event.stopPropagation();

    this.isAddingBank = true;
    this.cdr.detectChanges();


    // Get existing data from localStorage (removing hardcoded values)
    const mobile = localStorage.getItem('consent_mobile_number');
    const accountId = localStorage.getItem('consent_account_id');
    const productId = localStorage.getItem('consent_product_id');

    // Construct dynamic redirect URL (matching request-consent logic)
    const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
    const redirectUrl = `${window.location.origin}${baseHref}consent-callback`.replace(/([^:]\/)\/+/g, "$1");

    const consentData: any = {
      productID: productId,
      vua: `${mobile}@onemoney`,
      partyIdentifierType: "MOBILE",
      partyIdentifierValue: mobile,
      accountID: accountId,
      redirectUrl: redirectUrl
    };

    // Store/Refresh data in localStorage for callback verification
    localStorage.setItem('consent_mobile_number', consentData.partyIdentifierValue);
    localStorage.setItem('consent_product_id', consentData.productID);
    localStorage.setItem('consent_account_id', consentData.accountID);

    this.subs.add(this.fiDataService.requestConsent(consentData).subscribe({
      next: (response) => {
        this.isAddingBank = false;

        if (response && response.data && response.data.webRedirectionUrl) {
          localStorage.setItem('active_consent_handle', response.data.consent_handle);
          window.location.href = response.data.webRedirectionUrl;
        }

        this.closeDropdown();
        this.closeSettings();
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        // console.error('❌ Consent Request - Error:', error);
        this.isAddingBank = false;
        this.cdr.detectChanges();
      }
    }));

    this.cdr.detectChanges();
  }

  closeAddBankModal(event?: Event) {
    if (event) event.stopPropagation();
    if (this.addBankOverlayRef) {
      this.addBankOverlayRef.dispose();
      this.addBankOverlayRef = null;
    }
    this.cdr.detectChanges();
  }

  onBankConnected(bank: BankOption) {
    const newAccount: BankAccount = {
      id: Date.now().toString(),
      bankName: bank.name,
      accountNumber: '**** ' + Math.floor(1000 + Math.random() * 9000),
      balance: 0,
      type: 'Savings',
      logo: 'assets/icons/img-bank.svg',
    };

    this.accounts = [...this.accounts, newAccount];
    this.closeAddBankModal();
    this.cdr.detectChanges();
  }

  openSendUpiModal(event?: Event) {
    if (this.isAddMode) return;
    if (event) event.stopPropagation();
    // Do not close dropdown, allow it to be visible in background as per Figma

    if (!this.upiOverlayRef) {
      this.upiOverlayRef = this.createGlobalOverlay();
      this.subs.add(this.upiOverlayRef.backdropClick().subscribe(() => this.closeSendUpiModal()));

      const portal = new ComponentPortal(SendUpiModal);
      const componentRef = this.upiOverlayRef.attach(portal);

      this.subs.add(componentRef.instance.close.subscribe(() => this.closeSendUpiModal()));
    }
    this.cdr.detectChanges();
  }

  openScanQrModal(event?: Event) {
    if (this.isAddMode) return;
    if (event) event.stopPropagation();

    if (!this.scanQrOverlayRef) {
      this.scanQrOverlayRef = this.createGlobalOverlay();
      this.subs.add(this.scanQrOverlayRef.backdropClick().subscribe(() => this.closeScanQrModal()));

      const portal = new ComponentPortal(ScanQrModal);
      const componentRef = this.scanQrOverlayRef.attach(portal);

      if (componentRef.instance.close) {
        this.subs.add(componentRef.instance.close.subscribe(() => this.closeScanQrModal()));
      }
    }
    this.cdr.detectChanges();
  }

  closeScanQrModal(event?: Event) {
    if (event) event.stopPropagation();
    if (this.scanQrOverlayRef) {
      this.scanQrOverlayRef.dispose();
      this.scanQrOverlayRef = null;
    }
    this.cdr.detectChanges();
  }

  closeSendUpiModal(event?: Event) {
    if (event) event.stopPropagation();
    if (this.upiOverlayRef) {
      this.upiOverlayRef.dispose();
      this.upiOverlayRef = null;
    }
    this.cdr.detectChanges();
  }

  private cleanTransactionTitle(title: string): string {
    if (!title) return 'Transaction';

    // 1. Decode HTML entities
    let cleaned = title.replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#8217;/g, "'")
      .replace(/&quot;/g, '"');

    // 2. Remove long technical prefixes (e.g., EQUITY_DERIVATIVES/OPTIONS/392637247/)
    // This regex looks for prefix/prefix/numbers/
    const techPrefixRegex = /^(?:EQUITY|CURRENCY|COMMODITY|FIXED_INCOME)_(?:DERIVATIVES|MARKET)\/(?:OPTIONS|FUTURES|CASH)?\/?\d+\//i;
    const simpleTechPrefixRegex = /^(?:EQUITY|CURRENCY|NPS|ETF|MUTUAL_FUNDS)\/(?:\d+\/)?/i;

    cleaned = cleaned.replace(techPrefixRegex, '');
    cleaned = cleaned.replace(simpleTechPrefixRegex, '');

    // 3. Extract actual name (usually before the first slash if still complex, or common patterns)
    // If there's a slash left, it might be Name/ID/Code. We want the Name.
    if (cleaned.includes('/')) {
      const parts = cleaned.split('/');
      // Often the name is the first part that isn't just numbers or very short technical codes
      cleaned = parts[0].trim();
    }

    // 4. Final safety check for empty or too short string
    return (cleaned.length > 2) ? cleaned : title.split('/')[0] || 'Transaction';
  }
}
